import { useState, useRef } from "react";
import { Gauge, Play, Loader2, ArrowDown, ArrowUp, Activity } from "lucide-react";

const BASE = process.env.REACT_APP_BACKEND_URL;

function Dial({ value, max, label, unit, color }) {
  const pct = Math.max(0, Math.min(1, (value || 0) / max));
  const angle = -120 + pct * 240; // -120..120 deg
  const R = 70, cx = 90, cy = 90;
  const arc = (from, to, c, w) => {
    const p = (a) => [cx + R * Math.cos((a - 90) * Math.PI / 180), cy + R * Math.sin((a - 90) * Math.PI / 180)];
    const [x1, y1] = p(from), [x2, y2] = p(to);
    const large = to - from > 180 ? 1 : 0;
    return <path d={`M ${x1} ${y1} A ${R} ${R} 0 ${large} 1 ${x2} ${y2}`} stroke={c} strokeWidth={w} fill="none" strokeLinecap="round" />;
  };
  return (
    <div className="flex flex-col items-center" data-testid={`dial-${label}`}>
      <svg width="180" height="150" viewBox="0 0 180 150">
        {arc(-120, 120, "#1e293b", 12)}
        {arc(-120, angle, color, 12)}
        <line x1={cx} y1={cy} x2={cx + 60 * Math.cos((angle - 90) * Math.PI / 180)} y2={cy + 60 * Math.sin((angle - 90) * Math.PI / 180)} stroke={color} strokeWidth="3" strokeLinecap="round" />
        <circle cx={cx} cy={cy} r="6" fill={color} />
        <text x={cx} y={130} textAnchor="middle" fontSize="24" fontWeight="bold" fill="#e2e8f0">{value != null ? value.toFixed(value < 10 ? 1 : 0) : "—"}</text>
        <text x={cx} y={146} textAnchor="middle" fontSize="11" fill="#64748b">{unit}</text>
      </svg>
      <span className="text-xs text-slate-400 -mt-2">{label}</span>
    </div>
  );
}

export default function SpeedTest({ onComplete, saveToken, savePortal }) {
  const [running, setRunning] = useState(false);
  const [ping, setPing] = useState(null);
  const [down, setDown] = useState(null);
  const [up, setUp] = useState(null);
  const [phase, setPhase] = useState("");
  const abort = useRef(false);

  const run = async () => {
    setRunning(true); setPing(null); setDown(null); setUp(null); abort.current = false;
    try {
      // PING
      setPhase("ping");
      let best = 9999;
      for (let i = 0; i < 5; i++) {
        const t0 = performance.now();
        await fetch(`${BASE}/api/speedtest/ping?c=${Date.now()}${i}`, { cache: "no-store" });
        best = Math.min(best, performance.now() - t0);
      }
      setPing(best);
      // DOWNLOAD (25 MB)
      setPhase("download");
      const sizeMB = 25;
      const t1 = performance.now();
      const res = await fetch(`${BASE}/api/speedtest/download?size=${sizeMB}&c=${Date.now()}`, { cache: "no-store" });
      const reader = res.body.getReader();
      let bytes = 0;
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        bytes += value.length;
        const secs = (performance.now() - t1) / 1000;
        if (secs > 0) setDown((bytes * 8) / secs / 1e6);
      }
      const dSecs = (performance.now() - t1) / 1000;
      const dMbps = (bytes * 8) / dSecs / 1e6;
      setDown(dMbps);
      // UPLOAD (15 MB)
      setPhase("upload");
      const upBytes = 15 * 1024 * 1024;
      const blob = new Uint8Array(upBytes);
      const t2 = performance.now();
      await fetch(`${BASE}/api/speedtest/upload?c=${Date.now()}`, { method: "POST", body: blob, headers: { "Content-Type": "application/octet-stream" } });
      const uSecs = (performance.now() - t2) / 1000;
      const uMbps = (upBytes * 8) / uSecs / 1e6;
      setUp(uMbps);
      setPhase("done");
      const result = { ping_ms: Math.round(best), download_mbps: Math.round(dMbps * 10) / 10, upload_mbps: Math.round(uMbps * 10) / 10 };
      // save
      try {
        if (savePortal && saveToken) {
          await fetch(`${BASE}/api/portal/speedtest`, { method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${saveToken}` }, body: JSON.stringify(result) });
        }
      } catch { /* ignore */ }
      onComplete && onComplete(result);
    } catch (e) {
      setPhase("error");
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="rounded-xl border border-slate-700 bg-slate-900/60 p-4" data-testid="speedtest">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 font-heading font-bold text-slate-200"><Gauge size={18} className="text-cyan-400" /> Test prędkości</div>
        <button onClick={run} disabled={running} data-testid="speedtest-start" className="flex items-center gap-1.5 bg-cyan-600 hover:bg-cyan-500 disabled:opacity-50 text-white rounded-full px-4 py-1.5 text-sm">
          {running ? <Loader2 size={15} className="animate-spin" /> : <Play size={15} />} {running ? "Testuję…" : "Rozpocznij test"}
        </button>
      </div>
      <div className="grid grid-cols-3 gap-2">
        <Dial value={down} max={1000} label="Download" unit="Mb/s" color="#22d3ee" />
        <Dial value={up} max={1000} label="Upload" unit="Mb/s" color="#a78bfa" />
        <Dial value={ping} max={100} label="Ping" unit="ms" color="#f59e0b" />
      </div>
      {phase === "download" && <p className="text-xs text-cyan-400 mt-1 flex items-center gap-1"><ArrowDown size={12} /> Pomiar pobierania…</p>}
      {phase === "upload" && <p className="text-xs text-violet-400 mt-1 flex items-center gap-1"><ArrowUp size={12} /> Pomiar wysyłania…</p>}
      {phase === "ping" && <p className="text-xs text-amber-400 mt-1 flex items-center gap-1"><Activity size={12} /> Pomiar ping…</p>}
      {phase === "error" && <p className="text-xs text-red-400 mt-1">Błąd testu — spróbuj ponownie.</p>}
      <p className="text-[10px] text-slate-500 mt-2">Pomiar łącza między tym urządzeniem a serwerem operatora.</p>
    </div>
  );
}
